import clsx from 'clsx';
import React, { useEffect } from 'react';

import LazyImgixImage from '@components/LazyImgixImage';

import {
  CENTER,
  alignmentValues,
  backgroundColorValues,
  marginValues,
  paddingValues,
} from './constants';
import type { ImageBlockProps } from './interfaces';

const Image = ({ content, config }: ImageBlockProps) => (
  <LazyImgixImage
    alt={content?.alt}
    className={clsx(
      'image-block my-0 w-full',
      config?.css_classes,
      config?.alignment === CENTER ? 'mx-auto block' : alignmentValues[config?.alignment],
      !content?.caption && config?.width && `max-w-[${config.width}px]`,
      backgroundColorValues[config?.background_color],
      marginValues[config?.margin],
      paddingValues[config?.padding]
    )}
    src={content.src}
    style={{ maxWidth: `var(--max-${config?.width}-width)` }}
    visibleByDefault={false}
  />
);

const ImageWithLink = ({ content, config }: ImageBlockProps) => (
  <a className="inline-block w-full *:w-full" href={content.link} target="_blank" rel="noreferrer">
    <Image content={content} config={config} />
  </a>
);

const ImageBlock = ({ config, content }: ImageBlockProps) => {
  const width = Number(config?.width);

  useEffect(() => {
    if (width && !content?.caption) {
      const styleElement = document.createElement('style');
      styleElement.innerHTML = `:root { --max-${width}-width: ${width}px; }`;
      document.head.appendChild(styleElement);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!content.src) return null;

  if (content.caption) {
    return (
      <figure
        className={clsx(
          'image-block-with-caption relative m-0 w-full *:w-full',
          width && !isNaN(width) && `max-w-[${width}px]`
        )}
      >
        {content.link ? (
          <ImageWithLink content={content} config={config} />
        ) : (
          <Image content={content} config={config} />
        )}
        <figcaption className="text-caption-md">{content.caption}</figcaption>
      </figure>
    );
  } else if (content.link) {
    return <ImageWithLink content={content} config={config} />;
  } else {
    return <Image content={content} config={config} />;
  }
};

export default ImageBlock;
